import { mapTeamResponseDto } from 'modules/teams/domain/data-mappers/response-dto';
import { UpsertTournamentTeamDto } from 'modules/teams/domain/dtos/upsert-tournament-team.dto';
import { AnnouncedTeamsMap } from 'modules/teams/domain/interfaces/announced-teams-map';
import { TeamResponseDto } from 'modules/teams/domain/interfaces/response-dto';
import { Team } from 'modules/teams/domain/interfaces/team';
import getTeamState from 'modules/teams/domain/utils/get-team-state';
import { ResponseGraphql } from 'shared/types/response-graphql';
import { parseResponseError } from 'shared/utils';
import graphqlResource, { GraphqlResource } from 'modules/core/graphql-resource';
import { mapAnnouncedChampionshipTeams, mapChampionshipTeamsWithApplications } from '../data-mappers/announced-teams.mapper';
import { UpsertChampionshipApplicationResponseDto } from '../dtos/upsert-championship-application-response.dto';
import { UpsertChampionshipApplicationUserResponseDto } from '../dtos/upsert-championship-application-user-response.dto';
import { UpsertChampionshipApplicationUserDto } from '../dtos/upsert-championship-application-user.dto';
import { UpsertChampionshipApplicationDto } from '../dtos/upsert-championship-application.dto';

import { ApplicationDto } from '../interfaces/application-dto';
import { ChampionshipTeamDto } from '../interfaces/championship-team-dto';
import { PlayerApplication } from '../interfaces/player-application';

export class TeamsRepository {
  constructor(private readonly graphql: GraphqlResource) {}

  public async getAnnouncedChampionshipTeams(championshipId: number) {
    const championshipApplications = await this.getChampionshipApplications(championshipId);

    return mapAnnouncedChampionshipTeams(championshipId, championshipApplications.filter(Boolean)) as AnnouncedTeamsMap;
  }

  public async upsertChampionshipApplicationForUser(params: UpsertChampionshipApplicationUserDto): Promise<PlayerApplication> {
    const query = `#graphql
      mutation upsertChampionshipApplicationUser($input: ChampionshipApplicationOneRowInput!) {
        upsertChampionshipApplicationUser(input: $input) {
          id
          team { id }
          championship { id }
          user {
            id
            name
            surname
            middleName
            birthDate
            isReal
            photo {
              id
              externalUrl
            }
            height
            weight
          }
          state
          position
          number
          createdAt
        }
      }
    `;

    const { id, ...input } = params;
    const variables = { input };

    const response = await this.graphql.query<UpsertChampionshipApplicationUserResponseDto>(query, variables);
    if (response.data.errors) {
      throw new Error(parseResponseError(response.data.errors[0]));
    }

    const { upsertChampionshipApplicationUser: upsertedApplication } = response.data.data;

    return {
      id: upsertedApplication.id,
      teamId: upsertedApplication.team.id,
      championshipId: upsertedApplication.championship.id,
      player: {
        id: upsertedApplication.user.id,
        firstName: upsertedApplication.user.name,
        lastName: upsertedApplication.user.surname,
        middleName: upsertedApplication.user.middleName,
        birthDate: upsertedApplication.user.birthDate,
        logoUrl: upsertedApplication.user.photo?.externalUrl,
      },
      status: upsertedApplication.state,
      position: upsertedApplication.position,
      number: upsertedApplication.number,
      createdAt: upsertedApplication.createdAt,
    };
  }

  public async upsertChampionshipApplication(params: UpsertChampionshipApplicationDto) {
    const query = `#graphql
    mutation upsertChampionshipApplication($input: ChampionshipApplicationInput!, $championshipId: Int!) {
      upsertChampionshipApplication(input: $input) {
        team {
          id
          applicationsByChampionship(championshipId: $championshipId) {
            user {
              id
            }
            state
          }
        }
        championship {
          id
        }
      }
    }
    `;

    const variables = {
      championshipId: params.championshipId,
      input: {
        teamId: params.teamId,
        championshipId: params.championshipId,
        createUserOrUpdateApplications: params.applications.map((application) => ({
          userId: application.userId,
          // name: application.name,
          // surname: application.surname,
          // middleName: application.middleName,
          // phone: application.phone,
          // birthDate: application.birthDate,
          // height: application.height,
          // weight: application.weight,
          // number: application.number,
          // position: application.position?.toUpperCase(),
          state: application.state,
        })),
      },
    };
    const response = await this.graphql.query<UpsertChampionshipApplicationResponseDto>(query, variables);

    if (response.data.errors) {
      throw new Error(parseResponseError(response.data.errors[0]));
    }

    const { upsertChampionshipApplication: upsertedApplication } = response.data.data;

    const { applicationsByChampionship: users, ...team } = upsertedApplication.team;

    const teamState = getTeamState(users);

    return {
      championshipId: upsertedApplication.championship.id,
      team: { ...team, state: teamState },
      users: users.map(({ user, state }) => ({ id: user?.id, state })),
    };
  }

  /**
   * Флоу заявок v2: команды соревнования и заявки — одним запросом.
   * Команда без заявок в `championshipApplications` не приходит вовсе, поэтому список читается
   * ещё и из `teams` — иначе только что добавленная команда пропадает после F5 и после любого
   * перечитывания заявок. Прецедент такой же пары в одном запросе —
   * `championship.repository.ts:fetchChampionshipTeamsWithApplications`.
   */
  public async getChampionshipTeamsWithApplications(championshipId: number): Promise<AnnouncedTeamsMap> {
    const query = `#graphql
      query getChampionshipTeamsWithApplications($championshipId: Int!) {
        championship(id: $championshipId) {
          teams {
            id
            name
            shortName
            createdAt
            emblem {
              id
              externalUrl
            }
            members {
              id
              user {
                id
                isReal
              }
            }
          }
          championshipApplications {
            id
            team {
              id
              name
              shortName
              emblem {
                id
                externalUrl
              }
            }
            position
            number
            user {
              id
              name
              surname
              middleName
              birthDate
              photo {
                id
                externalUrl
              }
            }
            state
            createdAt
            updatedAt
          }
        }
      }
    `;

    const response = await this.graphql.query<{
      data: { championship: { teams: (ChampionshipTeamDto | null)[]; championshipApplications: ApplicationDto[] } };
    }>(query, { championshipId });

    const { teams, championshipApplications } = response.data.data.championship;

    return mapChampionshipTeamsWithApplications(championshipId, teams || [], (championshipApplications || []).filter(Boolean));
  }

  private async getChampionshipApplications(championshipId: number): Promise<ApplicationDto[]> {
    const query = `#graphql
      query getChampionshipAnnouncedTeams($championshipId: Int!) {
        championship(id: $championshipId) {
          championshipApplications {
            id
            team {
              id
              name
              shortName
              emblem {
                id
                externalUrl
              }
            }
            position
            number
            user {
              id
              name
              surname
              middleName
              birthDate
              photo {
                id
                externalUrl
              }
            }
            state
            createdAt
            updatedAt
          }
        }
      }
    `;

    const variables = { championshipId };
    const response = await this.graphql.query<{ data: { championship: { championshipApplications: ApplicationDto[] } } }>(
      query,
      variables,
    );
    const { championshipApplications } = response.data.data.championship;

    return championshipApplications;
  }

  public async getSportKitElements(sportType: string): Promise<string[]> {
    const query = `#graphql
    query sportKitElements($sportType: SportType!) {
      sportKitElements(sportType: $sportType)
    }
    `;

    const response = await this.graphql.query<ResponseGraphql<{ sportKitElements: string[] }>>(query, {
      sportType: sportType.toUpperCase(),
    });

    if (response.data.errors) {
      throw new Error(parseResponseError(response.data.errors[0]));
    }

    return (response.data.data.sportKitElements ?? []).map((e) => e.toLowerCase());
  }

  public async upsertTeamKits(
    teamId: number,
    kits: Array<{ type: string; elements: Array<{ element: string; pattern: string; colors: string[] }> }>,
  ): Promise<Team> {
    const query = `#graphql
    mutation upsertTeamKits($input: UpsertTeamKitsInput!) {
      upsertTeamKits(input: $input) {
        id
        name
        shortName
        tournament {
          sportType
        }
        kits {
          id
          type
          elements {
            element
            attributes {
              pattern
              colors
            }
          }
        }
      }
    }
    `;

    const variables = {
      input: {
        teamId,
        kits: kits.map((kit) => ({
          type: kit.type.toUpperCase(),
          elements: kit.elements.map((el) => ({
            element: el.element.toUpperCase(),
            pattern: el.pattern.toUpperCase(),
            colors: el.colors,
          })),
        })),
      },
    };

    const response = await this.graphql.query<ResponseGraphql<{ upsertTeamKits: TeamResponseDto }>>(query, variables);

    if (response.data.errors) {
      throw new Error(parseResponseError(response.data.errors[0]));
    }

    return mapTeamResponseDto([response.data.data.upsertTeamKits])[0];
  }

  public async upsertTeam(values: UpsertTournamentTeamDto): Promise<Team> {
    const query = `#graphql
    mutation upsertTeam($input: UpsertTeamInput!) {
      upsertTeam(input: $input) {
        id
        name
        shortName
        emblem {
          id
          externalUrl
        }
        createdAt
        members {
          id
        }
      }
    }
    `;

    const variables = {
      input: {
        id: values.id,
        name: values.name,
        shortName: values.shortName,
        tournamentId: values.tournamentId,
      },
    };

    const response = await this.graphql.query<ResponseGraphql<{ upsertTeam: TeamResponseDto }>>(query, variables);

    if (response.data.errors) {
      throw new Error(parseResponseError(response.data.errors[0]));
    }

    const result = response.data.data.upsertTeam;
    return mapTeamResponseDto([result])[0];
  }

  public async deleteTeam(id: number): Promise<{ status: string }> {
    const query = `#graphql
    mutation deleteTeam($id: Int!) {
      deleteTeam(id: $id) {
        status
      }
    }
    `;

    const response = await this.graphql.query<ResponseGraphql<{ deleteTeam: { status: string } }>>(query, { id });

    if (response.data.errors) {
      throw new Error(parseResponseError(response.data.errors[0]));
    }

    return response.data.data.deleteTeam;
  }
}

const teamsRepository = new TeamsRepository(graphqlResource);

export default teamsRepository;
